/**
 * Checklist library — templates auto-assigned to opportunities by name / type.
 * Order matters: most-specific matches first.
 */
import { MITACS_BASIC_TASKS } from "./checklists/mitacsBasic.js";
import { MITACS_DETAILED_TASKS } from "./checklists/mitacsDetailed.js";
import { GENERIC_GRANT_TASKS } from "./checklists/genericGrant.js";
import { SOFTWARE_SAVINGS_TASKS } from "./checklists/softwareSavings.js";
import { SRED_TASKS } from "./checklists/sred.js";
import { IRAP_TASKS } from "./checklists/irap.js";
import {
  CANEXPORT_SME_TASKS,
  CANEXPORT_INNOVATION_TASKS,
} from "./checklists/canexport.js";
import { SWPP_TASKS, BETS_TASKS } from "./checklists/hiring.js";
import {
  IRAP_CLEAN_TECH_TASKS,
  SDTC_TASKS,
  CLEAN_TECH_ITC_TASKS,
} from "./checklists/cleanTech.js";
import { QUEBEC_RD_TASKS } from "./checklists/quebec.js";
import {
  OVIN_TASKS,
  NOHFC_TASKS,
  ALBERTA_INNOVATES_TASKS,
} from "./checklists/regional.js";
import { IPON_TASKS, ACCELERATE_IP_TASKS } from "./checklists/ipLegal.js";
import { CDAP_TASKS, OCI_DMAP_TASKS } from "./checklists/digitization.js";
import {
  FEDDEV_BSP_TASKS,
  SWODF_EODF_TASKS,
  SOFII_TASKS,
  BDC_LOAN_TASKS,
  CSBFP_TASKS,
} from "./checklists/loans.js";
import { CANADA_JOB_GRANT_TASKS } from "./checklists/training.js";
import { CAP_AGRICULTURE_TASKS } from "./checklists/agriculture.js";

/** @typedef {'grant' | 'software'} ChecklistType */

/** Most-specific entries first; generic fallbacks last. */
export const CHECKLIST_LIBRARY = [
  // —— 3. Marketing & Expansion ——
  {
    id: "canexport-innovation",
    label: "CanExport Innovation",
    category: "Marketing & Expansion",
    type: "grant",
    match: {
      patterns: [/canexport\s*innovation/i, /canexport innovation/i],
      names: ["canexport innovation"],
    },
    tasks: CANEXPORT_INNOVATION_TASKS,
  },
  {
    id: "canexport-sme",
    label: "CanExport SME",
    category: "Marketing & Expansion",
    type: "grant",
    match: {
      patterns: [/canexport\s*sme/i, /canexport sme/i, /canexport(?!\s*innovation)/i],
      names: ["canexport sme", "canexport"],
    },
    tasks: CANEXPORT_SME_TASKS,
  },

  // —— 2. Hiring ——
  {
    id: "mitacs",
    label: "Mitacs",
    category: "Hiring",
    type: "grant",
    match: {
      patterns: [/mitacs/i],
      names: [
        "mitacs accelerate",
        "mitacs elevate",
        "mitacs business strategy",
        "mitacs bsi",
      ],
    },
    tasks: MITACS_BASIC_TASKS,
    detailedTasks: MITACS_DETAILED_TASKS,
  },
  {
    id: "swpp",
    label: "SWPP",
    category: "Hiring",
    type: "grant",
    match: {
      patterns: [/swpp/i, /student work placement/i],
      names: ["student work placement program", "swpp"],
    },
    tasks: SWPP_TASKS,
  },
  {
    id: "bets",
    label: "BETS",
    category: "Hiring",
    type: "grant",
    match: {
      patterns: [/bets/i, /bridging entrepreneurs to students/i],
      names: ["bets", "bridging entrepreneurs to students"],
    },
    tasks: BETS_TASKS,
  },

  // —— 1. R&D ——
  {
    id: "sred",
    label: "SR&ED",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/sr&?ed/i, /scientific research.*experimental development/i],
      names: [
        "scientific research & development",
        "scientific research and experimental development",
        "sred tax credit",
        "federal sred",
      ],
    },
    tasks: SRED_TASKS,
  },
  {
    id: "irap-clean-tech",
    label: "NRC IRAP Clean Technology",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/irap.*clean/i, /clean tech.*irap/i, /irap clean technology/i],
      names: ["irap clean technology", "irap clean tech"],
    },
    tasks: IRAP_CLEAN_TECH_TASKS,
  },
  {
    id: "irap",
    label: "NRC IRAP",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/irap/i, /nrc.?irap/i],
      names: ["nrc irap", "nrc - irap", "irap yep", "nrc irap financial support"],
    },
    tasks: IRAP_TASKS,
  },
  {
    id: "quebec-rd",
    label: "Quebec R&D Credits",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/cdae/i, /cdaeia/i, /cric/i, /quebec.*r&?d/i],
      names: ["cdae", "cdaeia", "cric", "quebec r&d", "quebec rd"],
    },
    tasks: QUEBEC_RD_TASKS,
  },
  {
    id: "sdtc",
    label: "SDTC",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/sdtc/i, /sustainable development technology canada/i],
      names: ["sdtc", "sustainable development technology"],
    },
    tasks: SDTC_TASKS,
  },
  {
    id: "clean-tech-itc",
    label: "Clean Technology ITCs",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [
        /clean tech.*itc/i,
        /ct\s*itc/i,
        /ctm\s*itc/i,
        /ccus/i,
        /carbon capture.*itc/i,
        /clean hydrogen.*itc/i,
        /clean technology.*tax credit/i,
      ],
      names: [
        "clean technology investment tax credit",
        "clean technology manufacturing",
        "carbon capture",
        "clean hydrogen",
      ],
    },
    tasks: CLEAN_TECH_ITC_TASKS,
  },
  {
    id: "ovin",
    label: "OVIN",
    category: "R&D",
    type: "grant",
    match: {
      patterns: [/ovin/i, /\boce\b/i, /ontario vehicle innovation/i],
      names: ["ovin", "ontario centre of innovation", "oce"],
    },
    tasks: OVIN_TASKS,
  },
  {
    id: "nohfc",
    label: "NOHFC",
    category: "Regional",
    type: "grant",
    match: {
      patterns: [/nohfc/i, /northern ontario heritage fund/i],
      names: ["nohfc", "northern ontario heritage fund"],
    },
    tasks: NOHFC_TASKS,
  },
  {
    id: "alberta-innovates",
    label: "Alberta Innovates",
    category: "Regional",
    type: "grant",
    match: {
      patterns: [/alberta innovates/i],
      names: ["alberta innovates"],
    },
    tasks: ALBERTA_INNOVATES_TASKS,
  },

  // —— 4. Legal & IP ——
  {
    id: "ipon",
    label: "IPON",
    category: "Legal & IP",
    type: "grant",
    match: {
      patterns: [/ipon/i, /intellectual property ontario/i],
      names: ["ipon", "intellectual property ontario"],
    },
    tasks: IPON_TASKS,
  },
  {
    id: "accelerate-ip",
    label: "AccelerateIP (BC)",
    category: "Legal & IP",
    type: "grant",
    match: {
      patterns: [/accelerateip/i, /accelerate ip/i],
      names: ["accelerateip", "accelerate ip"],
    },
    tasks: ACCELERATE_IP_TASKS,
  },

  // —— 5. Digitization ——
  {
    id: "cdap",
    label: "CDAP",
    category: "Digitization",
    type: "grant",
    match: {
      patterns: [/cdap/i, /canada digital adoption/i, /digital adoption program/i],
      names: ["cdap", "canada digital adoption program", "boost your business tech"],
    },
    tasks: CDAP_TASKS,
  },
  {
    id: "oci-dmap",
    label: "OCI Tech Demo / DMAP",
    category: "Digitization",
    type: "grant",
    match: {
      patterns: [/dmap/i, /tech demonstration/i, /oci.*demonstration/i],
      names: ["dmap", "tech demonstration", "oci dmap"],
    },
    tasks: OCI_DMAP_TASKS,
  },

  // —— 6. Loans ——
  {
    id: "feddev-bsp",
    label: "FedDev Ontario BSP",
    category: "Loans & Financing",
    type: "grant",
    match: {
      patterns: [/feddev/i, /business scale-up/i, /\bbsp\b/i],
      names: ["feddev ontario", "business scale-up and productivity", "feddev bsp"],
    },
    tasks: FEDDEV_BSP_TASKS,
  },
  {
    id: "swodf-eodf",
    label: "SWODF / EODF",
    category: "Loans & Financing",
    type: "grant",
    match: {
      patterns: [/swodf/i, /eodf/i, /eastern ontario development/i, /southwestern ontario development/i],
      names: ["swodf", "eodf"],
    },
    tasks: SWODF_EODF_TASKS,
  },
  {
    id: "sofii",
    label: "SOFII",
    category: "Loans & Financing",
    type: "grant",
    match: {
      patterns: [/sofii/i, /southern ontario fund/i],
      names: ["sofii", "southern ontario fund for investment in innovation"],
    },
    tasks: SOFII_TASKS,
  },
  {
    id: "bdc-loan",
    label: "BDC Loan",
    category: "Loans & Financing",
    type: "grant",
    match: {
      patterns: [/bdc/i, /business development bank/i],
      names: ["bdc", "bdc loan", "bdc small business loan"],
    },
    tasks: BDC_LOAN_TASKS,
  },
  {
    id: "csbfp",
    label: "CSBFP",
    category: "Loans & Financing",
    type: "grant",
    match: {
      patterns: [/csbfp/i, /canada small business financing/i],
      names: ["csbfp", "canada small business financing program"],
    },
    tasks: CSBFP_TASKS,
  },

  // —— 7. Training ——
  {
    id: "canada-job-grant",
    label: "Canada Job Grant",
    category: "Training",
    type: "grant",
    match: {
      patterns: [/canada job grant/i, /\bcjg\b/i, /job grant/i],
      names: [
        "canada job grant",
        "canada-ontario job grant",
        "bc employer training grant",
        "employer training grant",
      ],
    },
    tasks: CANADA_JOB_GRANT_TASKS,
  },

  // —— 8. Agriculture ——
  {
    id: "cap-agriculture",
    label: "Sustainable CAP (Agriculture)",
    category: "Agriculture",
    type: "grant",
    match: {
      patterns: [
        /agriassurance/i,
        /agriinnovate/i,
        /agriscience/i,
        /agrimarketing/i,
        /sustainable cap/i,
        /cap program/i,
      ],
      names: [
        "agriassurance",
        "agriinnovate",
        "agriscience",
        "agrimarketing",
        "sustainable canadian agricultural partnership",
      ],
    },
    tasks: CAP_AGRICULTURE_TASKS,
  },

  // —— Software ——
  {
    id: "software-savings",
    label: "Software Savings",
    category: "Software",
    type: "software",
    match: {
      upgradeTypes: ["Tech"],
      patterns: [/^tech\s*\(/i, /software saving/i, /vendor discount/i],
      names: ["software savings"],
    },
    tasks: SOFTWARE_SAVINGS_TASKS,
  },
];

export const DEFAULT_GRANT_CHECKLIST = {
  id: "generic-grant",
  label: "General Grant",
  type: "grant",
  tasks: GENERIC_GRANT_TASKS,
};

export const DEFAULT_SOFTWARE_CHECKLIST = {
  id: "generic-software",
  label: "Software Savings",
  type: "software",
  tasks: SOFTWARE_SAVINGS_TASKS,
};

export function getChecklistById(id) {
  if (!id) return null;
  const found = CHECKLIST_LIBRARY.find((c) => c.id === id);
  if (found) return found;
  if (id === DEFAULT_GRANT_CHECKLIST.id) return DEFAULT_GRANT_CHECKLIST;
  if (id === DEFAULT_SOFTWARE_CHECKLIST.id) return DEFAULT_SOFTWARE_CHECKLIST;
  return null;
}

export function listChecklistLibrary() {
  return CHECKLIST_LIBRARY.map(({ id, label, type, category }) => ({
    id,
    label,
    type,
    category,
  }));
}

/** Options for program selector UI (grouped by category). */
export function getChecklistSelectOptions() {
  const categoryOrder = [
    "R&D",
    "Hiring",
    "Marketing & Expansion",
    "Legal & IP",
    "Digitization",
    "Loans & Financing",
    "Training",
    "Regional",
    "Agriculture",
    "Software",
  ];

  const byCategory = {};
  for (const entry of CHECKLIST_LIBRARY) {
    const cat = entry.category || "Other";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push({
      id: entry.id,
      label: entry.label,
      type: entry.type,
    });
  }

  const categories = categoryOrder
    .filter((cat) => byCategory[cat]?.length)
    .map((label) => ({
      label,
      programs: byCategory[label],
    }));

  // Any uncategorized
  const known = new Set(categoryOrder);
  for (const [cat, programs] of Object.entries(byCategory)) {
    if (!known.has(cat)) {
      categories.push({ label: cat, programs });
    }
  }

  return {
    categories,
    defaults: [
      {
        id: DEFAULT_GRANT_CHECKLIST.id,
        label: DEFAULT_GRANT_CHECKLIST.label,
        type: DEFAULT_GRANT_CHECKLIST.type,
      },
      {
        id: DEFAULT_SOFTWARE_CHECKLIST.id,
        label: DEFAULT_SOFTWARE_CHECKLIST.label,
        type: DEFAULT_SOFTWARE_CHECKLIST.type,
      },
      { id: "custom", label: "Custom checklist", type: "custom" },
    ],
  };
}
